import { LanguageChoice } from '../i18n'
import { useProgressiveHash, parseBool } from '../utils'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useUserStore } from './useUserStore'
import { CodesModel } from '../models/CodesModel'
import { Task, useTaskProfiles } from './useTaskProfiles'

interface RaiCode {
  id: string
  bulk_update_allowed: string
  code: string
  description_en: string
  description_fr: string
  success: boolean
}

export type RaiCodes = RaiCode[]
interface RaiReasonCode {
  id: string
  reason: string
  reason_fr: string
}
export type RaiReasonCodes = RaiReasonCode[]
interface RaiCodesReasonCode {
  id: string
  rai_reason_codes_id: string
  rai_codes_id: string
}
export type RaiCodesReasonCodes = RaiCodesReasonCode[]
export interface RaiHardwareCode {
  id: string
  rai_codes_id: string
  task_types_id: string
  qty_to_rep: string
  qty_to_store: string
  qty_to_trash: string
  prev_code_success: string
  replacement_action: string
}
export interface RaiCodeVisibility {
  id: string
  rai_codes_id: string
  rai_codes_code: string
  product_lines_id: string
  task_types_id: string
  rai_logic_actions_id: string
  days_offset: string
}
export type RaiCodesVisibility = RaiCodeVisibility[]
export type RaiHardwareCodes = RaiHardwareCode[]
export type CodeKeys =
  | 'rai_codes'
  | 'rai_hardware_deductions'
  | 'rai_codes_reason_codes'
  | 'rai_reason_codes'
  | 'rai_codes_visibility'
export type Codes = {
  rai_codes: RaiCodes
  rai_hardware_deductions: RaiHardwareCodes
  rai_codes_reason_codes: RaiCodesReasonCodes
  rai_reason_codes: RaiReasonCodes
  rai_codes_visibility: RaiCodesVisibility
}
export type CodesHash = {
  [key in CodeKeys]: string
}
export type ReasonCodeOption = {
  label: {
    en_US: string
    fr_CA: string
  }
  value: string
}
export type ReasonCodesMap = {
  [key: RaiCode['code']]: ReasonCodeOption[]
}

const codes = ref<Codes>({
  rai_codes: [],
  rai_hardware_deductions: [],
  rai_codes_reason_codes: [],
  rai_reason_codes: [],
  rai_codes_visibility: [],
})
export const useCodes = defineStore('codes', () => {
  const resetStoreState = () => {
    const cleared = {
      rai_codes: [],
      rai_hardware_deductions: [],
      rai_codes_reason_codes: [],
      rai_reason_codes: [],
      rai_codes_visibility: [],
    }
    void setCodes(cleared, true)
  }
  const setCodes = async (data: Codes, skipWrite = false) => {
    for (const key in data) {
      //value could be null if hash is same so check for falsy here
      if (data[key as CodeKeys]) {
        codes.value[key as CodeKeys] = data[key as CodeKeys] as any
      }
    }
    if (!skipWrite) {
      await CodesModel().writeCodes(codes.value)
      await CodesModel().writeCodesHashes(await codesHash())
    }
  }
  const user = storeToRefs(useUserStore())
  const bulkUpdateRaiCodes = computed(() => {
    if (!codes.value) return []
    else {
      return codes.value.rai_codes
        .filter((code) => parseBool(code.bulk_update_allowed))
        .map((code) => {
          return {
            id: code.id,
            code: code.code,
            label: `${code.code} - ${determineLabel(code, user.language.value)}`,
          }
        })
    }
  })
  const getRaiCodeByIdString = (idString: string) => {
    return codes.value.rai_codes.find((code) => {
      return code.code == idString
    })
  }
  const isDeductionCode = (idString: string) => {
    const raiCodeId = getRaiCodeByIdString(idString)?.id
    console.log(
      'isDeductionCode',
      idString,
      raiCodeId,
      hardwareDeductionRaiCodes.value.map((code) => Number(code.rai_codes_id)),
      hardwareDeductionRaiCodes.value.map((code) => Number(code.id)).includes(Number(raiCodeId))
    )
    if (!raiCodeId) return false
    else {
      return hardwareDeductionRaiCodes.value
        .map((code) => Number(code.rai_codes_id))
        .includes(Number(raiCodeId))
    }
  }

  const deductsFromRep = (idString: string, taskTypeId: string) => {
    const raiCodeId = getRaiCodeByIdString(idString)?.id
    if (!raiCodeId) return false
    else {
      return hardwareDeductionRaiCodes.value
        .filter(
          (code) =>
            raiCodeId == code.rai_codes_id && String(code.task_types_id) == String(taskTypeId)
        )
        .map((code) => Number(code.qty_to_rep))
        .includes(Number(-1))
    }
  }

  const hardwareDeductionRaiCodes = computed(() => {
    console.log('hardwareDeductionRaiCodes')
    if (!codes.value) return []
    else {
      return codes.value.rai_hardware_deductions
    }
  })
  /**Gets by task data */
  const hardwareDeductionCode = (taskData: any) => {
    console.log('hardwareDeductionCode')
    const taskRaiCode = getRaiCodeByIdString(taskData.rai_code)
    if (!taskRaiCode) return null
    else {
      const deductionCode = hardwareDeductionRaiCodes.value.filter((code) => {
        return code.task_types_id == taskData.task_type_id && code.rai_codes_id == taskRaiCode.id
      })
      if (!deductionCode.length) return null
      else return deductionCode
    }
  }
  const successCodes = computed(() => {
    if (!codes.value) {
      return []
    } else {
      return codes.value.rai_codes.filter((code) => code.success)
    }
  })
  const codesHash = async (): Promise<CodesHash> => {
    const hashObj: any = {}
    for (const key in codes.value) {
      hashObj[key] = await useProgressiveHash(codes.value[key as keyof Codes])
    }
    return hashObj
  }

  const getCodesHashes = async () => {
    const hashes = await CodesModel().getCodesHashes()
    if (!hashes) {
      return await codesHash()
    }
    return hashes
  }

  /**
   * A computed form of reason code mapping. Rai code id is the key.
   */
  const raiCodeReasonCodeHashMap = computed((): { [key: string]: RaiReasonCodes } => {
    const returnHashMap: { [key: string]: RaiReasonCodes } = {}
    const raiCodeIds = codes.value.rai_codes.map((code) => code.id)
    for (const raiCodeId in raiCodeIds) {
      returnHashMap[String(raiCodeId)] = codes.value.rai_codes_reason_codes
        .filter((rai_code_reason_code) => {
          return rai_code_reason_code.rai_codes_id == raiCodeId
        })
        .map((rai_code_reason_code) => {
          return codes.value.rai_reason_codes.find(
            (rai_reason_code) => rai_reason_code.id == rai_code_reason_code.rai_reason_codes_id
          )!
        })
    }

    return returnHashMap
  })

  /**
   * A computed map of formatted RAI reason code options for recaps
   */
  const raiReasonCodeOptions = computed((): ReasonCodesMap => {
    const returnMap: ReasonCodesMap = {}
    for (const raiCode of codes.value.rai_codes) {
      returnMap[raiCode.code] = []
      const reasonCodeMappings = codes.value.rai_codes_reason_codes
        .filter((raiCodeReasonCode) => raiCodeReasonCode.rai_codes_id == raiCode.id)
        .map((raiCodeReasonCode) => raiCodeReasonCode.rai_reason_codes_id)
      const reasonCodes = codes.value.rai_reason_codes.filter((reasonCode) =>
        reasonCodeMappings.includes(reasonCode.id)
      )
      returnMap[raiCode.code] = reasonCodes.map((reasonCode) => ({
        label: {
          en_US: reasonCode.reason,
          fr_CA: reasonCode.reason_fr,
        },
        value: reasonCode.id,
      }))
    }
    return returnMap
  })

  const getReasonCodesByRaiCodeId = (id: string) => {
    if (!codes.value) return []
    else {
      if (!raiCodeReasonCodeHashMap.value[id]) return []
      console.log({ raiCodeReasonCodeHashMap })
      return raiCodeReasonCodeHashMap.value[id].map((code) => {
        return {
          id: code.id,
          code: code.id,
          label: determineLabelReasonCode(code, user.language.value),
        }
      })
    }
  }
  //gets all types of every code
  const getCodes = computed(() => {
    return codes.value
  })
  //can get single code of any type
  const getCode = (raikey: keyof Codes, key: string, value: string | number) => {
    //@ts-expect-error they all are arrays
    const res = codes.value[raikey].find((row: any) => {
      return String(row[key]) == String(value)
    })
    if (res) {
      return res
    }
  }

  function isRaiApplicableForTask(raiCode: string, task_id: string) {
    const task: Task = useTaskProfiles().getTaskByTaskId(task_id)
    const task_type_id = task.task_type_id
    const product_line_id = task.product_line_id
    const order_type_id = task.order_type_id
    let formattedCode = raiCode
    if (formattedCode === '0') {
      // Make sure the Completed RAI code is in the expected format
      formattedCode = '000'
    }
    //We never allow bulk upday for merch order types
    if (order_type_id == 4) {
      return false
    }
    return codes.value.rai_codes_visibility.some(
      (visibility) =>
        String(visibility.product_lines_id) === String(product_line_id) &&
        String(visibility.task_types_id) === String(task_type_id) &&
        String(visibility.rai_codes_code) === String(formattedCode)
    )
  }

  return {
    resetStoreState,
    setCodes,
    bulkUpdateRaiCodes,
    hardwareDeductionCode,
    isDeductionCode,
    deductsFromRep,
    successCodes,
    codesHash,
    getCodesHashes,
    raiReasonCodeOptions,
    getReasonCodesByRaiCodeId,
    getCodes,
    getCode,
    isRaiApplicableForTask,
  }
})

const determineLabel = (data: any, languagePreference: LanguageChoice = 'en_US') => {
  switch (languagePreference) {
    case 'fr_CA':
      return data['description_fr']
    // case 'es_MX':
    //   return data['description_es']
    case 'en_US':
      return data['description_en']
    default:
      return data['description_en']
  }
}

const determineLabelReasonCode = (data: any, languagePreference: LanguageChoice = 'en_US') => {
  switch (languagePreference) {
    case 'fr_CA':
      return data['reason_fr']
    // case 'es_MX':
    //   return data['description_es']
    case 'en_US':
      return data['reason']
    default:
      return data['reason']
  }
}
